import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, ListItem, ListItemText, Alert, Button, Checkbox, Chip } from '@mui/material';
import { fetchJson } from '../../utils/fetchJson';
import PerformancePanel from '../../components/Parent/PerformancePanel';
import ChildPicker from '../../components/Parent/ChildPicker';

export default function ParentChildrenAcademicsPage() {
  const { t } = useTranslation();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [marks, setMarks] = useState([]);
  const [grading, setGrading] = useState({ passMark: 10 });
  const [subjectSelectionRequired, setSubjectSelectionRequired] = useState(false);
  const [error, setError] = useState(null);
  const [offeredByStudent, setOfferedByStudent] = useState({}); // studentId -> [{id,name,code}]
  const [selectionsByStudent, setSelectionsByStudent] = useState({}); // studentId -> [subjectId]
  const [originalByStudent, setOriginalByStudent] = useState({}); // studentId -> [subjectId]
  const [loadingByStudent, setLoadingByStudent] = useState({}); // studentId -> bool

  useEffect(() => {
    let alive = true;
    fetchJson('/api/portal/parent/children')
      .then((data) => {
        if (!alive) return;
        const rows = Array.isArray(data?.children) ? data.children : [];
        setChildren(rows);
        if (rows.length > 0) setSelectedChild(rows[0].studentId || rows[0].id);
      })
      .catch((e) => { if (alive) setError(e?.message || t('parentAcademics.errors.loadChildren')); });
    return () => { alive = false; };
  }, [t]);

  useEffect(() => {
    let alive = true;
    if (!selectedChild) return () => { alive = false; };

    fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/academics`)
      .then((data) => {
        if (!alive) return;
        setSubjectSelectionRequired(!!data?.subjectSelectionRequired);
        setGrading(data?.grading || { passMark: 10 });
        setSubjects(Array.isArray(data?.subjects) ? data.subjects : []);
        setMarks(Array.isArray(data?.marks) ? data.marks : []);
      })
      .catch((e) => { if (alive) setError(e?.message || t('parentAcademics.errors.loadAcademics')); });

    return () => { alive = false; };
  }, [selectedChild, t]);

  useEffect(() => {
    if (!subjectSelectionRequired) return;
    const current = children.find(c => String(c.studentId || c.id) === String(selectedChild));
    const studentId = current?.studentId || current?.id;
    const classId = current?.classId;
    if (!studentId || !classId) return;
    if (offeredByStudent[String(studentId)]) return;
    loadElectivesForChild(studentId, classId);
  }, [subjectSelectionRequired, children, selectedChild]);

  const loadElectivesForChild = async (studentId, classId) => {
    setLoadingByStudent(prev => ({ ...prev, [studentId]: true }));
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId } : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
      const q = `?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;

      const [offered, enroll] = await Promise.all([
        fetchJson(`/api/class-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => []),
        fetchJson(`/api/allocations/student-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => ({ map: {} }))
      ]);

      let subsRaw = Array.isArray(offered) ? offered : (offered?.classSubjects || offered?.data || []);
      if (!Array.isArray(subsRaw)) subsRaw = [];
      const subs = subsRaw.map(it => {
        const s = (it.Subject || it.subject || {});
        return { id: s.id ?? it.subjectId ?? it.code, name: s.name || it.name || it.code || t('parentAcademics.subjectFallback', { id: s.id ?? it.subjectId ?? '' }), code: s.code || it.code || '' };
      }).filter(x => x && x.id);
      const seen = new Set();
      const uniqueSubs = [];
      for (const s of subs) { const k = String(s.id); if (!seen.has(k)) { seen.add(k); uniqueSubs.push(s); } }

      const map = (enroll && enroll.map) ? enroll.map : {};
      const selected = Array.from(new Set((map[String(studentId)] || []).map(String)));

      setOfferedByStudent(prev => ({ ...prev, [studentId]: uniqueSubs }));
      setSelectionsByStudent(prev => ({ ...prev, [studentId]: selected }));
      setOriginalByStudent(prev => ({ ...prev, [studentId]: selected }));
    } catch (e) {
      // show inline alert via error state if needed; keep silent to not block UX
    } finally {
      setLoadingByStudent(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const toggleChildSubject = (studentId, subjectId, checked) => {
    const sid = String(studentId);
    const prev = selectionsByStudent[sid] || [];
    const next = checked ? Array.from(new Set([...(prev || []), String(subjectId)])) : (prev || []).filter(x => String(x) !== String(subjectId));
    setSelectionsByStudent(p => ({ ...p, [sid]: next }));
  };

  const saveChildElectives = async (studentId, classId) => {
    const sid = String(studentId);
    const before = Array.from(new Set((originalByStudent[sid] || []).map(String)));
    const after = Array.from(new Set((selectionsByStudent[sid] || []).map(String)));
    const toAdd = after.filter(x => !before.includes(x)).map(x => ({ classId: Number(classId), studentId: Number(studentId), subjectId: Number(x), source: 'parent' }));
    const toRemove = before.filter(x => !after.includes(x)).map(x => ({ studentId: Number(studentId), subjectId: Number(x) }));
    if (toAdd.length === 0 && toRemove.length === 0) return;
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = { 'Content-Type': 'application/json', ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}), 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
      if (toAdd.length) await fetchJson('/api/allocations/student-subjects/bulk', { method: 'POST', headers, body: JSON.stringify(toAdd) });
      if (toRemove.length) await fetchJson('/api/allocations/student-subjects/bulk', { method: 'DELETE', headers, body: JSON.stringify({ classId: Number(classId), pairs: toRemove }) });
      setOriginalByStudent(prev => ({ ...prev, [sid]: [...(selectionsByStudent[sid] || [])] }));
    } catch (e) {
      // ignore; UI stays; user can retry save
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" gutterBottom>{t('parentAcademics.title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <ChildPicker
        children={children}
        value={selectedChild}
        onChange={setSelectedChild}
        label={t('parentAcademics.selectChild')}
        studentWord={t('parentAcademics.student')}
        sx={{ mb: 2 }}
      />

      {selectedChild && (
        <>
          <Typography variant="subtitle2" gutterBottom>{t('parentAcademics.subjects')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {subjects.map((s) => (
              <Chip key={s.id} label={s.code ? `${s.name} (${s.code})` : s.name} size="small" variant="outlined" />
            ))}
            {subjects.length === 0 && (
              <Alert severity="info" sx={{ width: '100%' }}>{t('parentAcademics.noSubjects')}</Alert>
            )}
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t('parentAcademics.academicPerformance')}</Typography>
          <PerformancePanel subjects={subjects} marks={marks} grading={grading} />

          {/* Elective selection panel */}
          {(() => {
            const current = children.find(c => String(c.studentId || c.id) === String(selectedChild));
            const studentId = current?.studentId || current?.id;
            const classId = current?.classId;
            if (!studentId || !classId) return null;
            if (!subjectSelectionRequired) return null;
            return (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>{t('parentAcademics.subjectSelection')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => loadElectivesForChild(studentId, classId)} disabled={!!loadingByStudent[studentId]}>{t('parentAcademics.load')}</Button>
                  <Button size="small" variant="contained" onClick={() => saveChildElectives(studentId, classId)} disabled={!offeredByStudent[studentId] || !!loadingByStudent[studentId]}>{t('parentAcademics.save')}</Button>
                </Box>
                {offeredByStudent[studentId] && (
                  <List dense sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, maxHeight: 240, overflow: 'auto' }}>
                    {offeredByStudent[studentId].map(s => (
                      <ListItem key={String(s.id)} disableGutters secondaryAction={null}>
                        <Checkbox edge="start" checked={(selectionsByStudent[String(studentId)] || []).map(String).includes(String(s.id))} onChange={(e) => toggleChildSubject(studentId, s.id, e.target.checked)} />
                        <ListItemText primary={s.name} secondary={s.code || ''} />
                      </ListItem>
                    ))}
                    {offeredByStudent[studentId].length === 0 && (
                      <ListItem><ListItemText primary={t('parentAcademics.noOfferedSubjects')} /></ListItem>
                    )}
                  </List>
                )}
              </Box>
            );
          })()}
        </>
      )}

      {children.length === 0 && !error && (
        <Alert severity="info">{t('parentAcademics.noChildrenData')}</Alert>
      )}
    </Box>
  );
}
